import React, { useState } from 'react';
import PropTypes from 'prop-types';
import visuallyHidden from '@mui/utils/visuallyHidden';
import { makeStyles } from '../../../tools/makeStyles';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import classNames from 'classnames';
import Header from '../common/Header.jsx';
import Footer from '../common/Footer.jsx';
import { defaultDescription, defaultTitle } from '../constants/constants';
import { setTabIndex } from '../../helpers/accessibility_helper';
import { DEFAULT_TRANSLATION } from '../../helpers/constants';

const useStyles = makeStyles(() => ({
  dropDownContainer: {
    width: '100%',
  },
}));

const DropDownPreview = ({
  questionId = -1,
  title = defaultTitle,
  titleRef,
  titleAriaLabel,
  commentAriaLabel,
  description = defaultDescription,
  options = [{ id: 1, text: 'option 1' }],
  handleAnswer = () => {},
  onCommentChange = () => {},
  savedAnswer,
  showSkipButton = false,
  showTextComment = false,
  showDescription = false,
  isRequired = false,
  isActive = true,
  commentsLabel,
  commentsPlaceholder,
  ariaLabel,
  translation = DEFAULT_TRANSLATION,
}) => {
  const styles = useStyles();

  const emptyAnswer = {
    value: null,
    questionId,
    comment: '',
  };

  const [answer, setAnswer] = useState(savedAnswer || emptyAnswer);
  const [hasSelectedAnswer, selectAnswer] = useState(false);

  const handleOptionalComment = (e) => {
    const newAnswer = {
      ...answer,
      comment: e.target.value,
    };

    setAnswer(newAnswer);
    onCommentChange(e.target.value);
  };

  const handleAnswerSelection = (e) => {
    selectAnswer(true);
    e.preventDefault();

    const newAnswer = {
      ...answer,
      value: e.target.value,
    };

    setAnswer(newAnswer);
    handleAnswer(newAnswer, !showTextComment);
  };

  const onConfirm = (hasAnswer = false) => {
    if (hasAnswer) {
      handleAnswer(answer);
    } else {
      setAnswer(emptyAnswer);
      selectAnswer(true);
      handleAnswer(emptyAnswer);
    }
  };

  const byPosition = (a, b) => a.position - b.position;

  return (
    <div>
      <Header
        questionId={questionId}
        isRequired={isRequired}
        title={title}
        titleRef={titleRef}
        titleAriaLabel={titleAriaLabel}
        description={description}
        showDescription={showDescription}
        isActive={isActive}
        translation={translation}
      />

      <div className={classNames(styles.dropDownContainer)}>
        <Box id="select-aria-label" aria-hidden className={styles.ariaLabel} sx={visuallyHidden}>
          {ariaLabel}
        </Box>
        <Select
          variant="standard"
          value={answer.value || ''}
          onChange={handleAnswerSelection}
          fullWidth
          labelId="select-aria-label"
          inputProps={{
            tabIndex: setTabIndex(isActive),
          }}
        >
          {options.sort(byPosition).map((opt, i) => (
            <MenuItem key={i} value={opt.id} tabIndex={0} aria-pressed={opt.id === answer?.id}>
              {opt.text}
            </MenuItem>
          ))}
        </Select>
      </div>

      <Footer
        value={answer.value}
        shouldFocusOnComment={hasSelectedAnswer}
        showSkipButton={showSkipButton && answer.value === null}
        showOkButton={showTextComment && answer.value !== null}
        onConfirm={onConfirm}
        showTextComment={showTextComment}
        commentAriaLabel={commentAriaLabel}
        optionalComment={answer.comment}
        onChangeComment={handleOptionalComment}
        isActive={isActive}
        label={commentsLabel}
        placeholder={commentsPlaceholder}
        translation={translation}
      />
    </div>
  );
};

DropDownPreview.propTypes = {
  questionId: PropTypes.number,
  title: PropTypes.string,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      position: PropTypes.number,
      text: PropTypes.string,
    }),
  ),
  savedAnswer: PropTypes.object,
  handleAnswer: PropTypes.func,
  onCommentChange: PropTypes.func,
  type: PropTypes.string,
  showTextComment: PropTypes.bool,
  showDescription: PropTypes.bool,
  showSkipButton: PropTypes.bool,
  isRequired: PropTypes.bool,
  isActive: PropTypes.bool,
};

export default DropDownPreview;
